import _ from 'lodash';
import { translateLabels } from '../../utils/commonMethods';
import { timeFormat } from '../../utils/dateFormat';
import { payrollIntegrationOptions } from '../../utils/constants';

export const schema = {
    title: 'User',
    type: 'object',
    required: ['email', 'first_name', 'last_name'],
    properties: {
        id: { type: 'integer' },
        status: {
            type: 'string',
            title: 'Active',
            default: 'active',
        },

        image: {
            type: ['string', 'null'],
            title: 'Photo',
        },
        username: {
            title: 'Username',
            type: 'string',
        },
        password: {
            title: 'Password',
            type: 'string',
        },
        user_type: {
            type: 'string',
            title: 'User type',
            default: 'member',
            anyOf: [
                {
                    title: 'Member',
                    enum: ['member']
                }
            ]
        },
        first_name: {
            title: 'First name',
            type: 'string',
        },
        last_name: {
            title: 'Last name',
            type: 'string',
        },
        email: {
            title: 'Email',
            type: 'string',
            format: 'email',
        },
        internal_number: { type: ['number', 'null'], title: 'Internal number' },
        generic_pin: { type: ['number', 'null'], title: 'PIN' },
        employee_percent: { type: 'integer', title: 'Employee percent', default: 100 },
        address: { type: ['string', 'null'], title: 'Address' },
        post_number: { type: ['string', 'null'], title: 'Post no', format: 'postnumber' },
        post_place: { type: ['string', 'null'], title: 'Post place' },
        full_name: { type: ['string', 'null'], title: 'Full name' },
        phone: { type: ['string', 'null'], title: 'Phone', format: 'phone-no' },
        g_nr: { type: ['string', 'null'], title: 'G.Nr' },
        b_nr: { type: ['string', 'null'], title: 'B.Nr' },
        description: { type: ['string', 'null'], title: 'Description' },
        timelog_start_from: { type: ['string', 'null'], format: 'date', title: 'Start date' },
        timelog_end_at: { type: ['string', 'null'], format: 'date', title: 'End date' },
        new_pm_id: { type: ['number', 'null'], title: 'Reassign to' },
        timelog_last_filled: { type: ['string', 'null'], format: 'date', title: 'Work logs last filled' },
        birthday: { type: ['string', 'null'], format: 'date', title: 'Date of birth' },
        social_number: { type: ['string', 'null'], format: 'social-no', title: 'Date of birth no.' },
        attachments: { type: ['string', 'null'], title: 'Attachments' },
        vacation_days: { type: "number", title: "Vacation Days" },
        hourly_rate: { type: ['number', 'null'], title: 'Hourly/Fixed Rate' },
        payment_mode: { type: "string", title: "Remuneration type" },
        fixed_hourly_rate: { type: ['number', 'null'], title: 'Calculated hourly rate', default: 0, readOnly: true },
        weekend_extra_payment_percent: { type: ['number', 'null'], title: 'Weekend extra percent (%)', default: 0 },
        expected_fixed_hours: { type: ['number', 'null'], title: 'Estimated hours', default: 0 },
        calculated_weekly_hourly_payment: { type: ['number', 'null'], title: 'Calculated Weekend Hourly Rate (Gross + Weekly)', default: 0, readOnly: true },
        calculated_employement_hours: { type: ['number', 'null'], title: 'Calculated Employement Hours', default: 0, readOnly: true },
        disable_autolog: {
            type: ['boolean', 'null'],
            title: 'Disable auto-log',
            default: true,
        },
        home_payment_enabled: {
            type: ['boolean', 'null'],
            title: 'I am paid to come to work place',
            default: false,
        },
        gps_data: { type: ['string', 'null'], title: 'GPS location', format: 'gps' },
        data: {
            type: 'object',
            properties: {
                holidays: {
                    type: 'string',
                    title: 'Default holidays',
                },
                kids: {
                    type: 'array',
                    title: 'Info About Kids',
                    default: [],
                    items: {
                        type: 'object',
                        required: ['name', 'date'],
                        properties: {
                            name: {
                                title: 'Name',
                                type: 'string',
                            },
                            date: {
                                title: 'Date of birth',
                                type: 'string',
                                format: 'date',
                            },
                            chronic_disease: {
                                type: 'string',
                                title: 'Has chronic deasease?',
                                anyOf: [
                                    {
                                        title: 'No',
                                        enum: ['no'],
                                    },
                                    {
                                        title: 'Yes',
                                        enum: ['yes'],
                                    },
                                ],
                                default: 'no',
                            },
                        },
                    },
                },
                is_single_parent: {
                    type: 'boolean',
                    title: 'Are you a single parent',
                    default: false,
                },
                nomines: {
                    type: 'array',
                    title: 'Nomines',
                    default: [],
                    items: {
                        type: 'object',
                        required: ['name', 'contactNumber'],
                        properties: {
                            name: {
                                title: 'Name',
                                type: 'string',
                                format: 'required-name',
                            },
                            contactNumber: {
                                title: 'Contact Number',
                                type: 'string',
                                format: 'phone-no',
                            },
                        },
                    },
                },
                enable_pause_button: { type: 'boolean', title: 'Enable tracked pause', default: false },
                disable_manual_log: { type: 'boolean', title: 'Hide manual time tracking', default: false },
            },
        },
        ManagesUsers: {
            type: 'array',
            title: 'Manages',
            default: [],
            items: {
                type: 'object',
                required: ['id'],
                properties: {
                    id: {
                        title: 'Member',
                        type: 'integer',
                    },
                },
            },
        },
        allow_tip: {
            type: 'boolean',
            title: 'Allow Tips',
            default: false,
            anyOf: [
                {
                    title: 'Yes',
                    enum: [true],
                },
                {
                    title: 'No',
                    enum: [false],
                },
            ],
        },
        employee_type: {
            type: 'string',
            title: 'Employee type',
        },
        groups: {
            type: 'string',
            title: 'Groups',
        },
        payment_cycle: {
            type: 'string',
            title: 'Payment period',
            default: 'monthly',
            anyOf: [
                {
                    title: 'Monthly',
                    key: 'Monthly',
                    enum: ['monthly'],
                },
                {
                    title: 'Weekly',
                    key: 'Weekly',
                    enum: ['weekly'],
                },
            ],
        },
        allow_mobile_app_access: {
            type: 'boolean',
            title: 'Allow mobile access',
            default: false,
        },
        departmentIntegration: {
            type: ['number', 'null'],
            title: 'Department (Tripletex)',
        },
        bank_account_number: { type: ['string', 'null'], title: 'Bank Account', format: 'bank_account_number' },
        extra_payments: { type: ['string', 'null'], title: 'Additional Payments', default: 'weekends' },
        company_shift_id: { type: ['integer', 'null'], title: 'Working Shift' },
        allow_auto_flexitime: {
            type: 'boolean',
            title: 'Allow automatic flexitime',
            default: false,
        },
        max_negative_hours_flexitime: { type: 'number', title: 'Maximum negative flexitime hours', default: 0 },
        max_positive_hours_flexitime: { type: 'number', title: 'Maximum positive flexitime hours', default: 0 },
        paid_overtime_renewal_hours: { type: 'number', title: 'Paid Overtime Renewal (hours)', default: 0, minimum: 0 },
        work_time_agreement: { type: 'number', title: 'Work time agreement (Without Break)', default: 0, multipleOf: 0.25, minimum: 0 },
        auto_break: {
            type: 'boolean',
            title: 'Auto break',
            default: true,
        },
        exclude_break_in_working_hours_rule: {
            type: 'boolean',
            title: 'Shift code excludes break',
            default: false,
        },
        allow_user_for_auto_timesheet: {
            type: 'boolean',
            title: 'Auto time recording',
            default: false,
        },
        follow_schedule_for_absence: {
            type: 'boolean',
            title: 'Follow schedule shift hours while taking absence',
            default: false,
        },
        user_skills: {
            type: 'string',
            title: 'Skills',
        },
        leave_type_grants: {
            type: ['string', 'null'],
            title: 'Leave Type',
        },
    }
};

export const getSchema = props => {
    if (!props) return schema;
    const { config, mode, userType, currentEntityUser, currentUser, translate, sortCompanyShifts } = props;
    const _schema = _.cloneDeep(schema);

    _schema.properties.payment_mode = {
        type: 'string',
        title: 'Remuneration type',
        default: 'hourly',
        anyOf: [
            {
                title: "hourly",
                key: "hourly",
                enum: ["hourly"]
            },
            {
                title: "fixed",
                key: "fixed",
                enum: ["fixed"]
            }
        ],
    };

    if (config.client.allow_company_shifts && config.companyShifts.length) {
        const { allow_resource_planner } = config.client?.data?.basicRules?.workPlanSettings || {};
        const options = sortCompanyShifts(config.companyShifts, false, { F1: false, F2: false, F3: false, F4: false, F5: false }).map(e => {
            const startTimeFormatted = timeFormat(e.start_time, config.client.data.dateTimeRules);
            const endTimeFormatted = timeFormat(e.end_time, config.client.data.dateTimeRules);
            return {
                title: `${e.shift_name} (${startTimeFormatted} - ${endTimeFormatted})`,
                key: e.id,
                enum: [e.id]
            };
        });

        options.unshift({
            title: 'None',
            key: null,
            enum: [null]
        })
        _schema.properties.company_shift_id = {
            type: ['integer', 'null'],
            title: 'Working Shift',
            default: options[0].key,
            anyOf: options
        }

        if (!allow_resource_planner) {
            delete _schema.properties.user_skills;
        }
    } else {
        delete _schema.properties.company_shift_id;
    }

    if (translate) {
        translateLabels(_schema.properties.payment_cycle.anyOf, translate);
        translateLabels(_schema.properties.payment_mode.anyOf, translate);
    }

    if (mode === 'members' || mode === 'superadmins') {
        delete _schema.properties.data;
    }

    if (userType === 'admin') {
        _schema.properties.user_type = {
            type: 'string',
            title: 'User type',
            default: 'member',
            anyOf: [
                {
                    title: 'Admin',
                    enum: ['admin'],
                },
                {
                    title: 'PM',
                    enum: ['pm'],
                },
                {
                    title: 'Member',
                    enum: ['member'],
                },
            ],
        };
    } else {
        _schema.properties.user_type.anyOf.push({
            title: 'PM',
            enum: ['pm']
        })
        _schema.properties.user_type.readOnly = true;
    }


    if (mode === 'files' || mode === 'profile' || userType === 'member') {
        delete _schema.properties.allow_tip;
        delete _schema.properties.employee_type;
        delete _schema.properties.allow_mobile_app_access;
        delete _schema.properties.company_shift_id;
        _schema.properties.employee_percent.readOnly = true;
        _schema.properties.hourly_rate.readOnly = true;
    }

    if (mode === 'profile') {
        _schema.properties.allow_push = { type: 'boolean', title: 'Allow push notifications to be sent to your mobile device', default: true };
        _schema.properties.allow_email = { type: 'boolean', title: 'Allow notifications to be sent to your email address', default: true };
        _schema.properties.allow_system = { type: 'boolean', title: 'Allow notifications to be shown within the application', default: true };
    }

    if (!config) return _schema;

    if (config.client.data.roles && config.client.data.roles.length) {
        const employee_roles = config.client.data.roles.map((role) => ({
            title: role.name,
            enum: [role.code]
        }));

        employee_roles.unshift({
            title: 'No Department',
            enum: ['no_department']
        }, {
            title: 'Others',
            enum: ['z_none']
        })

        _schema.properties.employee_type = {
            type: 'string',
            title: 'Department',
            anyOf: employee_roles,
            default: 'no_department'
        }
    }
    else {
        const employee_roles = [
            {
                title: 'No Department',
                enum: ['no_department']
            },
            {
                title: 'Others',
                enum: ['z_none']
            }
        ]

        _schema.properties.employee_type = {
            type: 'string',
            title: 'Department',
            anyOf: employee_roles,
            default: 'no_department'
        }
    }

    if (currentEntityUser && currentEntityUser.payment_mode === 'hourly') {
        _schema.properties.expected_fixed_hours.readOnly = true;
    }

    if (config?.integration_details?.product !== payrollIntegrationOptions.TRIPLETEX) {
        delete _schema.properties.departmentIntegration;
    }

    if (config?.client?.data?.basicRules?.hideSensitiveDetailsFromPM && currentUser?.user_type === 'pm' && currentUser?.id !== currentEntityUser?.id) {
        _schema.properties.social_number.readOnly = true;
        _schema.properties.bank_account_number.readOnly = true;
        _schema.properties.hourly_rate.readOnly = true;
        _schema.properties.calculated_weekly_hourly_payment.readOnly = true;
        _schema.properties.hourly_rate.type = ['string', 'null'];
        _schema.properties.fixed_hourly_rate.type = ['string'];
        _schema.properties.calculated_weekly_hourly_payment.type = ['string'];
        delete _schema.properties.fixed_hourly_rate.default;
        delete _schema.properties.calculated_weekly_hourly_payment.default;
        delete _schema.properties.social_number.format;
        delete _schema.properties.company_shift_id;
    }

    if (!config?.client?.allow_company_shifts || !config?.client?.data?.basicRules?.timeBankSettings?.allowAutomaticFlexitimeTimeBank) {
        delete _schema.properties.allow_auto_flexitime;
        delete _schema.properties.max_negative_hours_flexitime;
        delete _schema.properties.max_positive_hours_flexitime;
        delete _schema.properties.exclude_break_in_working_hours_rule;
    }

    const overtimeBankSettings = config?.client?.data?.basicRules?.overtimeBankSettings;
    const paidOvertimeBalanceConfigured = !!(overtimeBankSettings?.allowOvertimeBank
        && overtimeBankSettings?.allowPaidOvertimeBalanceManagement
        && overtimeBankSettings?.autoOvertimeBankRenewal);
    if (!paidOvertimeBalanceConfigured) {
        delete _schema.properties.paid_overtime_renewal_hours;
    }

    if (config?.client?.data?.loginRules?.autoTimelogs !== 'autoschedule' || !config?.client?.data?.loginRules?.allowUserToEnableDisableAutoTimesheet) {
        delete _schema.properties.allow_user_for_auto_timesheet;
    }

    if (mode === 'profile' && userType !== 'pm' && userType !== 'member') {
        delete _schema.properties.allow_user_for_auto_timesheet;
    }

    if (mode === 'superadmins' || userType === 'super-admin') {
        delete _schema.properties.address;
        delete _schema.properties.post_number;
        delete _schema.properties.post_place;
        delete _schema.properties.user_type;
        delete _schema.properties.allow_tip;
        delete _schema.properties.employee_type;
        delete _schema.properties.timelog_start_from;
        delete _schema.properties.timelog_end_at;
        delete _schema.properties.new_pm_id;
        delete _schema.properties.employee_percent;
        delete _schema.properties.payment_mode;
        delete _schema.properties.hourly_rate;
        delete _schema.properties.paid_overtime_renewal_hours;
        delete _schema.properties.vacation_days;
        delete _schema.properties.allow_mobile_app_access;
        delete _schema.properties.bank_account_number;
        delete _schema.properties.extra_payments;
        delete _schema.properties.groups;
        delete _schema.properties.expected_fixed_hours;
        delete _schema.properties.weekend_extra_payment_percent;
        delete _schema.properties.calculated_weekly_hourly_payment;
        delete _schema.properties.payment_cycle;
        delete _schema.properties.fixed_hourly_rate;
        delete _schema.properties.company_shift_id;
        _schema.properties.user_type = {
            type: 'string',
            title: 'User type',
            default: 'super-admin',
            anyOf: [
                {
                    title: 'Super-admin',
                    enum: ['super-admin'],
                },
            ],
        }
    }

    return _schema;
};

export default getSchema;
